/**
 * Click protocol v2
 *
 * Outbound (agent -> UI): <click action="X" id="Y">label</click>
 *   action: vertical | category | parent | service | detail
 *   id formats:
 *     vertical : <vertical>
 *     category : <vertical>|<category>
 *     parent   : <parent_title>
 *     service  : <uuid>
 *     detail   : <detail_type>:<uuid>
 *
 * Inbound (UI -> agent) when the user taps a click button:
 *   __click__|action=<action>|id=<id>|label=<visible label>
 *
 * Rules:
 *   - id is sent verbatim. The decoder is positional by field name, so a
 *     category id containing literal '|' parses correctly without escaping.
 *   - label is informational; the agent routes by action+id only.
 *   - __click__| is a reserved prefix. Any free-form user text that does not
 *     start with that prefix is treated as plain language input.
 */

export const CLICK_PREFIX = '__click__|';

// Matches <click ...optional attrs...>label</click> (case-insensitive, multi-line label).
// Group 1: raw attribute string (may be empty/undefined for legacy <click>).
// Group 2: inner label text.
export const CLICK_TAG_RE = /<click(\s[^>]*)?>([\s\S]*?)<\/click>/gi;

// Matches a single name="value" attribute pair.
const ATTR_RE = /(\w+)\s*=\s*"([^"]*)"/g;

/**
 * Parse the raw attribute string from a <click ...> tag into a plain object.
 * Unknown attributes are preserved (lower-cased keys); typically { action, id }.
 * @param {string} [rawAttrs]
 * @returns {{ action?: string, id?: string, [k: string]: string|undefined }}
 */
export function parseClickAttrs(rawAttrs = '') {
  const out = {};
  if (!rawAttrs) return out;
  ATTR_RE.lastIndex = 0;
  let m;
  while ((m = ATTR_RE.exec(rawAttrs)) !== null) {
    out[m[1].toLowerCase()] = m[2];
  }
  return out;
}

/**
 * Build the wire payload for a tapped click button.
 * @param {{ action: string, id: string, label?: string }} args
 * @returns {string}
 */
export function encodeClickPayload({ action, id, label }) {
  return `${CLICK_PREFIX}action=${action}|id=${id}|label=${label ?? ''}`;
}
